import {FluentBundle, FluentResource, type FluentVariable} from '@fluent/bundle';
import {ReactLocalization} from '@fluent/react';
import {observable, runInAction} from 'mobx';
import locales from 'locales';
import {DEFAULT_LANGUAGE, SupportedLanguage} from './constants';
import {ltr} from './bidi';

const createBundle = (lang: SupportedLanguage): FluentBundle => {
    const bundle = new FluentBundle(lang, {useIsolating: false});
    const errors = bundle.addResource(new FluentResource(locales[lang]));

    if (errors.length > 0) {
        console.warn(`Fluent resource errors (${lang})`, errors);
    }

    return bundle;
};

const localizations = new Map<SupportedLanguage, ReactLocalization>();

/** The localization of one language, built once per page. English is appended as a
 *  fallback bundle so a key a translation hasn't caught up with yet renders the
 *  English copy instead of the raw message id. */
export const localizationFor = (lang: SupportedLanguage): ReactLocalization => {
    let localization = localizations.get(lang);
    if (!localization) {
        const bundles = lang === DEFAULT_LANGUAGE
            ? [createBundle(lang)]
            : [createBundle(lang), createBundle(DEFAULT_LANGUAGE)];
        localization = new ReactLocalization(bundles);
        localizations.set(lang, localization);
    }
    return localization;
};

let globalLocalization: ReactLocalization | null = null;

// The language MobX has been told about. translate() reads it, so a computed or an
// observer that rendered translated copy runs again after a language switch.
const announcedLanguage = observable.box<SupportedLanguage | null>(null);

/** Switches translate() to `lang` at once. Called while the LanguageProvider renders,
 *  so the subtree rendering in the same pass already gets the new copy. */
export const setGlobalLanguage = (lang: SupportedLanguage) => {
    globalLocalization = localizationFor(lang);
};

/** Tells MobX dependants of translate() that the language changed. Called after the
 *  commit: notifying observers during a render would re-render them inside it. */
export const announceLanguage = (lang: SupportedLanguage) => {
    runInAction(() => announcedLanguage.set(lang));
};

/** The language as MobX sees it — for reactions that push it to a third party. */
export const observedLanguage = (): SupportedLanguage | null => announcedLanguage.get();

export const translate = (
    id: string,
    args?: Record<string, FluentVariable>,
    fallback?: string,
): string => {
    announcedLanguage.get();
    if (!globalLocalization) {
        return fallback ?? id;
    }

    try {
        return globalLocalization.getString(id, args, fallback ?? id);
    } catch (error) {
        console.error(`Missing translation for key: ${id}`, error);
        return fallback ?? id;
    }
};

export const formatPercent = (
    value: number | string,
    fractionDigits: number = 0,
    nonZero=false,
): string => {
    let numericValue = typeof value === 'number' ? value : Number(value);

    // Round between 1 and 99
    if (numericValue > 5 && numericValue < 95) {
        numericValue = Math.round(numericValue);
    }

    const hasNumericValue = Number.isFinite(numericValue);

    let formatted = hasNumericValue
        ? numericValue.toFixed(fractionDigits)
        : (typeof value === 'string' ? value : '0');

    if (hasNumericValue && formatted.includes('.')) {
        formatted = formatted
            .replace(/(\.\d*?[1-9])0+$/, '$1')
            .replace(/\.0+$/, '');
    }
    if ((formatted == "0" || formatted == "0.0" || formatted == "100") && nonZero) {
        return formatPercent(value, fractionDigits + 1, false);
    }

    return ltr(translate('common-percent', {value: formatted}, `${formatted}%`));
};
